import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { PlusCircle } from 'react-feather';
import { api, Question } from '../api/client';
import { formatShortDate } from '../utils/date';
import { useAuth } from '../context/AuthContext';
import CreateQuestion from './CreateQuestion';
import QuickLogPanel from './QuickLogPanel';
import { useSnackbar } from '../context/SnackbarContext';

const VerifyEmailGate: React.FC<{ userEmail?: string; userId?: string }> = ({ userEmail, userId }) => {
  const { showSnackbar } = useSnackbar();
  const [resendState, setResendState] = useState<'idle' | 'sending'>('idle');

  const handleResend = async () => {
    if (!userId) return;
    setResendState('sending');
    try {
      await api.resendVerificationEmail(userId);
      showSnackbar('A new verification email has been sent.', { variant: 'success' });
    } catch {
      showSnackbar('Failed to resend. Please try again.', { variant: 'error' });
    } finally {
      setResendState('idle');
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-box">
        <h1>Verify Your Email</h1>
        <p className="auth-subtitle">
          Your account is created{userEmail ? ` for ${userEmail}` : ''}, but email
          verification is required before you can use questions.
        </p>
        <div className="info">
          Please open the verification email we sent you and click the link.
        </div>
        <button
          className="btn btn-secondary btn-full"
          onClick={() => void handleResend()}
          disabled={resendState === 'sending'}
          style={{ marginTop: '1rem' }}
        >
          {resendState === 'sending' ? 'Sending...' : 'Resend verification email'}
        </button>
      </div>
    </div>
  );
};

const QuestionList: React.FC = () => {
  const { user } = useAuth();
  const [questions, setQuestions] = useState<Question[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateQuestion, setShowCreateQuestion] = useState(false);
  const {
    yourQuestions,
    sharedQuestions,
    publicQuestions,
    quickLogQuestions,
  } = useMemo(() => {
    const owned = questions.filter((question) => question.created_by === user?.id);
    const shared = questions.filter((question) => !question.is_public && question.created_by !== user?.id);
    const publicItems = questions.filter((question) => question.is_public);

    return {
      yourQuestions: owned,
      sharedQuestions: shared,
      publicQuestions: publicItems,
      quickLogQuestions: [...owned, ...shared],
    };
  }, [questions, user?.id]);

  useEffect(() => {
    loadQuestions();
  }, []);

  const loadQuestions = async () => {
    try {
      setLoading(true);
      const data = await api.listQuestions();
      setQuestions(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load questions');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="loading">Loading...</div>;

  if (error) {
    const needsEmailVerification = error.toLowerCase().includes('email not verified');
    if (needsEmailVerification) {
      return <VerifyEmailGate userEmail={user?.email} userId={user?.id} />;
    }
    return <div className="error">{error}</div>;
  }

  const renderQuestionGrid = (items: Question[], shared = false) => (
    <div className="questions-grid">
      {items.map((question) => (
        <Link
          key={question.id}
          to={`/questions/${question.id}`}
          className="question-card"
        >
          <h3>{question.title}</h3>
          {question.description && <p>{question.description}</p>}
          <div className="question-meta">
            <span className={shared ? 'badge shared' : question.is_public ? 'badge public' : 'badge personal'}>
              {shared ? 'Shared' : question.is_public ? 'Public' : 'Personal'}
            </span>
            <span className="date">
              {formatShortDate(question.created_at)}
            </span>
          </div>
        </Link>
      ))}
    </div>
  );

  return (
    <div className="question-list container">
      <QuickLogPanel questions={quickLogQuestions} />

      <div className="page-header">
        <h2>Your Questions</h2>
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => setShowCreateQuestion(true)}
        >
          <PlusCircle size={14} />
          Create question
        </button>
      </div>

      {yourQuestions.length === 0 ? (
        <p className="empty-state">
          You have not created any questions yet.{' '}
          <button
            type="button"
            className="inline-link"
            onClick={() => setShowCreateQuestion(true)}
          >
            Create one!
          </button>
        </p>
      ) : (
        renderQuestionGrid(yourQuestions)
      )}

      {sharedQuestions.length > 0 && (
        <section className="question-list-section">
          <h2>Shared with You</h2>
          {renderQuestionGrid(sharedQuestions, true)}
        </section>
      )}

      <section className="question-list-section">
        <h2>Public Questions</h2>
        {publicQuestions.length === 0 ? (
          <p className="empty-state">No public questions yet.</p>
        ) : (
          renderQuestionGrid(publicQuestions)
        )}
      </section>

      {showCreateQuestion && (
        <CreateQuestion
          modal
          onClose={() => setShowCreateQuestion(false)}
        />
      )}
    </div>
  );
};

export default QuestionList;
